import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { adminV2Theme as t } from "../theme";

function KpiCard({ label, value, sub, accent }) {
  return (
    <Box
      sx={{
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        borderRadius: `${t.radius.md}px`,
        boxShadow: t.shadow.card,
        p: 2.5,
        minHeight: 110,
        borderTop: `3px solid ${accent || t.colors.primary}`,
      }}
    >
      <Typography sx={{ fontSize: 13, color: t.colors.textMuted, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 0.5, color: t.colors.text }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: t.colors.textMuted, mt: 0.5 }}>{sub}</Typography>
      )}
    </Box>
  );
}

export default function OverviewPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/admin/v2/overview`, {
          credentials: "include",
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Failed (${res.status})`);
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message || "Failed to load overview");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ color: t.colors.danger }}>{error}</Typography>;
  }

  const k = data?.kpis || {};

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Overview
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, mb: 3, fontSize: 14 }}>
        Car owner signups, service requests, and shop activity.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
          gap: 2,
        }}
      >
        <KpiCard
          label="Car owner signups (total)"
          value={k.car_owner_signups_total ?? "—"}
          sub={`This month: ${k.car_owner_signups_this_month ?? 0} · Prev month: ${k.car_owner_signups_prev_month ?? 0}`}
          accent={t.colors.primary}
        />
        <KpiCard
          label="Service requests (total)"
          value={k.service_requests_total ?? "—"}
          sub={`This month: ${k.service_requests_this_month ?? 0}`}
          accent={t.colors.accent}
        />
        <KpiCard
          label="Active shops"
          value={k.shops_active ?? "—"}
          sub={`Pending: ${k.shops_pending ?? 0}`}
          accent={t.colors.primary}
        />
        <KpiCard
          label="Mechanics (total)"
          value={k.mechanics_total ?? "—"}
          accent={t.colors.accent}
        />
      </Box>
    </Box>
  );
}
